/*
 * a1-form-guard.js — never let a contact/quote form fail silently (2026-10-09).
 *
 * Shopify's captcha bootstrap (content_for_header) cancels a contact-form submit while
 * hCaptcha is not bound yet, shows nothing, and never retries if the hCaptcha provider
 * fails to load (ad blocker, privacy browser, corporate filter). Shopify's server also
 * rejects a /contact POST without a captcha token (HTTP 400 "Missing CAPTCHA token"), so
 * this script does NOT bypass the captcha. It only:
 *   1. holds a submit made before hCaptcha is bound ("Verifying…") and re-submits once,
 *      the moment hCaptcha is ready;
 *   2. if hCaptcha does not become ready, tells the visitor THIS attempt was not sent and
 *      offers call/text and an email draft (entered values are kept; never auto-posts after that);
 *   3. sends anonymous Clarity diagnostic events, only if Clarity is already loaded
 *      (i.e. the visitor accepted cookies). No names, emails or message text are sent.
 * When hCaptcha is bound it stays out of the way (Shopify + hCaptcha submit as before).
 * Scope: Shopify contact forms (form_type=contact), excluding newsletter sign-ups.
 * If contact-form hCaptcha is switched OFF in Shopify (as of 2026-10-09 it is), the guard stands down
 * completely and only sends the anonymous diagnostics; it re-activates by itself if the setting is turned back on.
 */
(function () {
  'use strict';
  if (window.__a1FormGuard) return;
  window.__a1FormGuard = true;

  var WAIT_MS = 12000;          // max wait for hCaptcha to bind after a held submit
  var SLOW_MS = 20000;          // bound path: neutral hint if nothing visible happened
  var PHONE_DISPLAY = '(803) 526-5797';
  var PHONE_TEL = '+18035265797';
  var EMAIL = 'sales@a1compressorwarehouse.com';
  var ATTEMPT_KEY = 'a1fg_attempt';
  var PROVIDER_RE = /^https:\/\/(js\.hcaptcha\.com\/|cdn\.shopify\.com\/shopifycloud\/storefront-forms-hcaptcha\/)/;
  var MAIL_FIELDS = /^contact\[(product_title|product|variant_id)\]$/; // hidden fields worth keeping in the email draft
  var providerFailed = false;
  var seen = {};
  var holds = new WeakMap();    // form -> {poll, timer, submitter, started}

  // ---------- diagnostics ----------
  function clarityOn() { return typeof window.clarity === 'function'; }
  function clarityEvent(name) {
    try { if (clarityOn()) { window.clarity('event', name); return true; } } catch (e) {}
    return false;
  }
  function clarityOnce(key, name) {
    if (!seen[key] && clarityEvent(name)) seen[key] = true;
  }

  // ---------- scope ----------
  function isNewsletter(f) {
    var tags = f.querySelector('[name="contact[tags]"]');
    return (tags && /(^|,)\s*newsletter\s*(,|$)/i.test(tags.value || '')) || f.hasAttribute('data-newsletter-form');
  }
  function isLeadForm(f) {
    if (!f || f.nodeName !== 'FORM') return false;
    if ((f.getAttribute('action') || '').indexOf('/contact') === -1) return false;
    var type = f.querySelector('input[name="form_type"]');
    return !!type && type.value === 'contact' && !isNewsletter(f);
  }
  // Is Shopify's hCaptcha switched on for CONTACT forms? (Online Store > Preferences > Spam protection.)
  // Shopify's inline captcha bootstrap ends with ...['guest_login'])})(<accounts>,<contact>)}();  -> true/false.
  // null = could not tell (Shopify changed its markup); callers then only hold forms the bootstrap has touched.
  var contactFlag;
  function contactCaptchaOn() {
    if (contactFlag !== undefined) return contactFlag;
    contactFlag = null;
    var l = document.querySelectorAll('script:not([src])');
    for (var i = 0; i < l.length; i++) {
      var m = /\['guest_login'\]\)\}\)\((!0|!1|true|false),\s*(!0|!1|true|false)\)\}\(\)/.exec(l[i].textContent || '');
      if (m) { contactFlag = (m[2] === '!0' || m[2] === 'true'); break; }
    }
    if (contactFlag === null) clarityEvent('captcha_flag_unknown'); // Shopify changed its markup: re-check this script
    return contactFlag;
  }
  // Would Shopify's bootstrap cancel an unbound submit of this form?
  function captchaGuarded(f) {
    var on = contactCaptchaOn();
    if (on === false) return false;                 // contact captcha off: Shopify posts normally, guard stands down
    if (on === true) return true;
    return f.dataset.cptcha === 'true' || isBound(f); // unknown: only forms the bootstrap has actually wired
  }
  function isBound(f) {
    return f.dataset.hcaptchaBound === 'true' || f.dataset.recaptchaBound === 'true';
  }
  function formKey(f) {
    // id first: the LP success page re-renders <form id="leadform"> without its hidden fields.
    var src = f.querySelector('[name="contact[form_source]"]');
    return f.id || (src && src.value) || 'contact';
  }

  // Definite provider load failure (resource errors don't bubble: capture on window).
  window.addEventListener('error', function (e) {
    var t = e && e.target;
    if (t && t.tagName === 'SCRIPT' && PROVIDER_RE.test(t.src || '')) providerFailed = true;
  }, true);

  // ---------- status UI ----------
  var cssDone = false;
  function injectCss() {
    if (cssDone) return;
    cssDone = true;
    var s = document.createElement('style');
    s.textContent =
      '.a1fg-box{margin:10px 0 0;padding:10px 12px;border-radius:8px;font-size:15px;line-height:1.45;text-align:left}' +
      '.a1fg-box[hidden]{display:none}' +
      '.a1fg-wait{background:#eef3fb;color:#1F3C88;border:1px solid #c9d6ef}' +
      '.a1fg-fail{background:#fff4f2;color:#7a1d12;border:1px solid #f0c2b9;outline:none}' +
      '.a1fg-box a{color:inherit;font-weight:700;text-decoration:underline}' +
      '.a1fg-actions{display:flex;flex-wrap:wrap;gap:8px;margin-top:8px}' +
      '.a1fg-actions a{display:inline-block;padding:9px 12px;border-radius:6px;background:#fff;border:1px solid #f0c2b9;text-decoration:none}';
    (document.head || document.documentElement).appendChild(s);
  }
  function box(f, kind) { // kind: 'wait' (polite status) | 'fail' (alert)
    var cls = 'a1fg-' + kind;
    var el = f.querySelector('.' + cls);
    if (!el) {
      injectCss();
      el = document.createElement('div');
      el.className = 'a1fg-box ' + cls;
      if (kind === 'wait') { el.setAttribute('role', 'status'); el.setAttribute('aria-live', 'polite'); }
      else { el.setAttribute('role', 'alert'); el.setAttribute('tabindex', '-1'); }
      el.hidden = true;
      var btn = f.querySelector('button[type="submit"],input[type="submit"]');
      if (btn && btn.parentNode) btn.parentNode.insertBefore(el, btn.nextSibling); else f.appendChild(el);
    }
    return el;
  }
  function hide(f) {
    var l = f.querySelectorAll('.a1fg-box');
    for (var i = 0; i < l.length; i++) l[i].hidden = true;
  }
  function setText(el, parts) { // parts: strings or {href, text}
    while (el.firstChild) el.removeChild(el.firstChild);
    for (var i = 0; i < parts.length; i++) {
      var p = parts[i];
      if (typeof p === 'string') { el.appendChild(document.createTextNode(p)); continue; }
      var a = document.createElement('a');
      a.href = p.href;
      a.textContent = p.text;
      el.appendChild(a);
    }
  }
  function showWait(f, parts) {
    var fb = f.querySelector('.a1fg-fail');
    if (fb) fb.hidden = true;
    var el = box(f, 'wait');
    setText(el, parts);
    el.hidden = false;
  }
  function buildMailto(f) {
    // Built only when the visitor taps the button; opened in their own mail app; never stored or sent by us.
    var lines = [];
    var els = f.querySelectorAll('[name^="contact["]');
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      var v = typeof el.value === 'string' ? el.value.trim() : '';
      if (!v || el.name === 'contact[form_source]' || el.name === 'contact[Source]') continue;
      if (el.type === 'hidden' && !MAIL_FIELDS.test(el.name)) continue;
      lines.push(el.name.replace(/^contact\[/, '').replace(/\]$/, '').replace(/_/g, ' ') + ': ' + v);
    }
    var src = f.querySelector('[name="contact[form_source]"]');
    var subject = 'Website request' + (src && src.value ? ' (' + src.value + ')' : '');
    return 'mailto:' + EMAIL + '?subject=' + encodeURIComponent(subject) + '&body=' + encodeURIComponent(lines.join('\n'));
  }
  function showFail(f, definite) {
    var w = f.querySelector('.a1fg-wait');
    if (w) w.hidden = true;
    var el = box(f, 'fail');
    while (el.firstChild) el.removeChild(el.firstChild);
    var head = document.createElement('strong');
    head.textContent = 'Your request was not sent. ';
    el.appendChild(head);
    el.appendChild(document.createTextNode(definite
      ? 'Our spam check could not load in this browser (often an ad blocker or a company network). '
      : 'We could not complete the spam check. '));
    el.appendChild(document.createTextNode('Your details are still filled in. Please reach us directly:'));
    var row = document.createElement('div');
    row.className = 'a1fg-actions';
    var call = document.createElement('a');
    call.href = 'tel:' + PHONE_TEL;
    call.textContent = 'Call / text ' + PHONE_DISPLAY;
    var mail = document.createElement('a');
    mail.href = 'mailto:' + EMAIL;
    mail.textContent = 'Open an email draft (then tap Send)';
    mail.addEventListener('click', function (ev) {
      ev.preventDefault();
      window.location.href = buildMailto(f);
    });
    row.appendChild(call);
    row.appendChild(mail);
    el.appendChild(row);
    el.hidden = false;
    try { el.focus({ preventScroll: false }); } catch (e) { el.focus(); }
    f.__a1fgFailed = true; // never auto-post after this; a fresh tap is a fresh attempt
  }

  // ---------- submit handling ----------
  function clearHold(f) {
    var h = holds.get(f);
    if (!h) return;
    clearInterval(h.poll);
    clearTimeout(h.timer);
    holds.delete(f);
  }

  function resubmit(f, submitter) {
    clearHold(f);
    var w = f.querySelector('.a1fg-wait');
    if (w) w.hidden = true;
    f.__a1fgResubmitting = true;
    try {
      if (typeof f.requestSubmit === 'function') {
        if (submitter && submitter.form === f && !submitter.disabled) f.requestSubmit(submitter); else f.requestSubmit();
      } else {
        var b = (submitter && submitter.form === f) ? submitter : f.querySelector('button[type="submit"],input[type="submit"]');
        f.__a1fgSawSubmit = false;
        if (b) b.click();
        // Old Safari: confirm the click actually produced a submit event.
        setTimeout(function () { if (!f.__a1fgSawSubmit) { showFail(f, false); clarityEvent('captcha_failed'); } }, 1000);
      }
    } catch (e) {
      showFail(f, false);
      clarityEvent('captcha_failed');
    }
    f.__a1fgResubmitting = false;
  }

  function hold(f, submitter) {
    if (holds.has(f)) return; // already waiting: ignore repeat taps
    f.__a1fgFailed = false;
    showWait(f, ['Verifying your request… one moment.']);
    clarityEvent('captcha_wait');
    var h = { submitter: submitter, started: Date.now() };
    h.poll = setInterval(function () {
      if (f.__a1fgFailed) { clearHold(f); return; }
      if (isBound(f)) { clarityEvent('captcha_ready_after_wait'); resubmit(f, h.submitter); return; }
      if (providerFailed && Date.now() - h.started > 1000) { clearHold(f); showFail(f, true); clarityEvent('captcha_failed'); }
    }, 250);
    h.timer = setTimeout(function () {
      if (!holds.has(f) || isBound(f)) return;
      clearHold(f);
      showFail(f, providerFailed);
      clarityEvent(providerFailed ? 'captcha_failed' : 'captcha_timeout');
    }, WAIT_MS);
    holds.set(f, h);
    // Start binding if the visitor never focused a field (autofill + Enter). Shopify's documented API;
    // skipped if Shopify already started binding this form (data-cptcha).
    try {
      if (!f.dataset.cptcha && window.Shopify && window.Shopify.captcha && typeof window.Shopify.captcha.protect === 'function') {
        window.Shopify.captcha.protect(f);
      }
    } catch (e) {}
  }

  function rememberAttempt(f) { // diagnostics only, and only after consent (Clarity present)
    if (!clarityOn()) return;
    try { sessionStorage.setItem(ATTEMPT_KEY, JSON.stringify({ t: Date.now(), k: formKey(f) })); } catch (e) {}
  }

  // Capture phase on document: runs before Shopify's bubble-phase document submit listener.
  document.addEventListener('submit', function (e) {
    var f = e.target;
    if (!isLeadForm(f)) return;
    f.__a1fgSawSubmit = true;
    clarityEvent('lead_submit_attempt');
    if (!captchaGuarded(f)) { rememberAttempt(f); return; } // no captcha on this form: never interfere
    if (isBound(f)) {
      // Double-tap guard: swallow a repeat USER submit within 2 s of one we let through.
      // (Shopify's hCaptcha provider completes with native form.submit(), which fires no submit event,
      // and our own requestSubmit() re-entry is untrusted, so neither can be swallowed here.)
      var now = Date.now();
      if (e.isTrusted && !f.__a1fgResubmitting && f.__a1fgLastPass && now - f.__a1fgLastPass < 2000) {
        e.preventDefault(); e.stopImmediatePropagation(); return;
      }
      f.__a1fgLastPass = now;
      clearHold(f); hide(f);
      rememberAttempt(f);
      watchChallenge();
      clearTimeout(f.__a1fgSlow);
      f.__a1fgSlow = setTimeout(function () {
        if (document.visibilityState === 'visible' && !challengeVisible() && !holds.has(f)) {
          showWait(f, ['Still verifying — we can’t confirm your request has been sent yet. If nothing changes, call or text ',
            { href: 'tel:' + PHONE_TEL, text: PHONE_DISPLAY }, '.']);
          clarityEvent('lead_submit_slow');
        }
      }, SLOW_MS);
      return; // Shopify + hCaptcha handle the real submit
    }
    e.preventDefault();
    e.stopImmediatePropagation();
    if (providerFailed) { showFail(f, true); clarityEvent('captcha_failed'); return; }
    hold(f, e.submitter || null);
  }, true);

  window.addEventListener('pagehide', function () {
    var forms = document.querySelectorAll('form');
    for (var i = 0; i < forms.length; i++) { clearTimeout(forms[i].__a1fgSlow); clearHold(forms[i]); }
  });

  document.addEventListener('focusin', function (e) {
    var f = e.target && e.target.form;
    if (isLeadForm(f)) clarityOnce('start:' + formKey(f), 'lead_form_start');
  }, true);

  // ---------- puzzle detection (diagnostic hint only; never controls delivery) ----------
  function challengeVisible() {
    var l = document.querySelectorAll('iframe[src*="hcaptcha.com"]');
    for (var i = 0; i < l.length; i++) {
      var r = l[i].getBoundingClientRect();
      if (r.width > 150 && r.height > 150 && r.bottom > 0 && r.right > 0 &&
          getComputedStyle(l[i]).visibility !== 'hidden') return true;
    }
    return false;
  }
  var mo = null;
  function watchChallenge() {
    if (mo || !clarityOn() || seen.challenge || !window.MutationObserver || !document.body) return;
    mo = new MutationObserver(function () {
      if (challengeVisible()) { clarityOnce('challenge', 'captcha_challenge_shown'); stop(); }
    });
    var stop = function () { if (mo) { mo.disconnect(); mo = null; } };
    mo.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['style', 'class'] });
    setTimeout(stop, 30000);
  }

  // ---------- confirmed success (diagnostic) ----------
  // = server-rendered success marker inside a form with the same key as an attempt made in this tab.
  function checkSuccess() {
    var raw = null;
    try { raw = sessionStorage.getItem(ATTEMPT_KEY); } catch (e) { return; }
    if (!raw) return;
    var a; try { a = JSON.parse(raw); } catch (e) { return; }
    if (!a || Date.now() - a.t > 30 * 60 * 1000) { try { sessionStorage.removeItem(ATTEMPT_KEY); } catch (e) {} return; }
    var markers = document.querySelectorAll('.thanks.show[data-lead-success], .form-status--success');
    for (var i = 0; i < markers.length; i++) {
      var f = markers[i].closest('form');
      if (f && formKey(f) === a.k && clarityEvent('lead_posted')) {
        try { sessionStorage.removeItem(ATTEMPT_KEY); } catch (e) {}
        return;
      }
    }
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', checkSuccess); else checkSuccess();
  setTimeout(checkSuccess, 3000);
  setTimeout(checkSuccess, 8000);

  // bfcache restore: clear stale state
  window.addEventListener('pageshow', function (e) {
    if (!e.persisted) return;
    var forms = document.querySelectorAll('form');
    for (var i = 0; i < forms.length; i++) if (isLeadForm(forms[i])) { clearHold(forms[i]); hide(forms[i]); }
  });
})();
